/**
 * Native Firebase Service
 *
 * Platform-aware Firebase service that uses native Capacitor plugins on iOS/Android
 * and falls back to the web SDK on web platforms.
 *
 * Benefits of native plugins:
 * - Sub-second cold start (vs 5-15s with web SDK in WebView)
 * - Auth persisted in iOS Keychain (reliable)
 * - Offline cache in native SQLite (vs IndexedDB issues)
 * - Lower memory usage
 */

import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication, User as CapacitorUser } from '@capacitor-firebase/authentication';
import { FirebaseFirestore, DocumentSnapshot, QueryCompositeFilterConstraint } from '@capacitor-firebase/firestore';
import {
  getDb,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch,
  addDoc,
  getFirebaseAuthAsync,
  type User as WebUser
} from '@watchlist/shared';
import { User, onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';

// Type for unified user across platforms
export interface UnifiedUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  creationTime?: number; // Unix ms — available on both native and web
}

// Convert Capacitor user to unified format
function capacitorUserToUnified(user: CapacitorUser | null): UnifiedUser | null {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email ?? null,
    displayName: user.displayName ?? null,
    photoURL: user.photoUrl ?? null,
    emailVerified: user.emailVerified ?? false,
    creationTime: user.metadata?.creationTime ?? undefined,
  };
}

// Convert Web user to unified format
function webUserToUnified(user: User | null): UnifiedUser | null {
  if (!user) return null;
  const creationTime = user.metadata?.creationTime
    ? new Date(user.metadata.creationTime).getTime()
    : undefined;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    creationTime,
  };
}

class NativeFirebaseService {
  private isNative: boolean;
  private authListenerCallbackId: string | null = null;
  private snapshotListeners: Map<string, string> = new Map();

  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    console.log(`NativeFirebaseService: Platform is ${this.isNative ? 'native' : 'web'}`);
  }

  // ============================================
  // AUTHENTICATION
  // ============================================

  /**
   * Get current authenticated user
   * On native: Uses Capacitor Firebase Auth plugin (fast, uses Keychain)
   * On web: Uses Firebase web SDK
   */
  async getCurrentUser(): Promise<UnifiedUser | null> {
    if (this.isNative) {
      try {
        const result = await FirebaseAuthentication.getCurrentUser();
        console.log('NativeFirebaseService: Got current user from native:', result.user?.email);
        return capacitorUserToUnified(result.user);
      } catch (error) {
        console.error('NativeFirebaseService: Error getting current user:', error);
        return null;
      }
    } else {
      const auth = await getFirebaseAuthAsync();
      return webUserToUnified(auth.currentUser);
    }
  }

  /**
   * ID token for HTTPS calls to Firebase Cloud Functions (Bearer auth).
   * On native, the JS SDK's `getAuth().currentUser` is often null while the native plugin holds the session — use the plugin token.
   */
  async getIdTokenForHttp(forceRefresh = false): Promise<string | null> {
    if (this.isNative) {
      try {
        const result = await FirebaseAuthentication.getIdToken({ forceRefresh });
        return result.token ?? null;
      } catch (error) {
        console.error('NativeFirebaseService: getIdTokenForHttp failed:', error);
        return null;
      }
    }
    const auth = await getFirebaseAuthAsync();
    if (!auth.currentUser) {
      return null;
    }
    return auth.currentUser.getIdToken(forceRefresh);
  }

  /**
   * Sign in with Google
   * On native: Uses native Google Sign-In flow
   * On web: Uses popup-based sign in
   */
  async signInWithGoogle(): Promise<UnifiedUser | null> {
    if (this.isNative) {
      try {
        const result = await FirebaseAuthentication.signInWithGoogle({
          webClientId: process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || '418607615172-qjkm9cpud0ghklds8nv6ajtu1v80ogn7.apps.googleusercontent.com'
        });
        console.log('NativeFirebaseService: Signed in with Google (native):', result.user?.email);
        return capacitorUserToUnified(result.user);
      } catch (error) {
        console.error('NativeFirebaseService: Error signing in with Google:', error);
        throw error;
      }
    } else {
      const auth = await getFirebaseAuthAsync();
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      return webUserToUnified(result.user);
    }
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    if (this.isNative) {
      await FirebaseAuthentication.signOut();
      console.log('NativeFirebaseService: Signed out (native)');
    } else {
      const auth = await getFirebaseAuthAsync();
      await signOut(auth);
    }
  }

  /**
   * Listen for auth state changes
   * Returns cleanup function
   */
  onAuthStateChanged(callback: (user: UnifiedUser | null) => void): () => void {
    if (this.isNative) {
      // Native auth listener
      FirebaseAuthentication.addListener('authStateChange', (change) => {
        console.log('NativeFirebaseService: Auth state changed (native):', change.user?.email);
        callback(capacitorUserToUnified(change.user));
      });

      // Return cleanup function
      return () => {
        FirebaseAuthentication.removeAllListeners();
      };
    } else {
      // Web auth listener
      let unsubscribe: (() => void) | null = null;

      getFirebaseAuthAsync().then((auth) => {
        unsubscribe = onAuthStateChanged(auth, (user) => {
          console.log('NativeFirebaseService: Auth state changed (web):', user?.email);
          callback(webUserToUnified(user));
        });
      });

      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }

  // ============================================
  // FIRESTORE OPERATIONS
  // ============================================

  /**
   * Get a document by path
   */
  async getDocument<T>(collectionPath: string, documentId: string): Promise<T | null> {
    const reference = `${collectionPath}/${documentId}`;

    if (this.isNative) {
      try {
        const result = await FirebaseFirestore.getDocument({ reference });
        if (!result.snapshot) return null;
        return result.snapshot.data as T;
      } catch (error: any) {
        // Document not found is not an error
        if (error.message?.includes('NOT_FOUND')) return null;
        console.error('NativeFirebaseService: Error getting document:', error);
        throw error;
      }
    } else {
      const db = getDb();
      const docRef = doc(db, collectionPath, documentId);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? (docSnap.data() as T) : null;
    }
  }

  /**
   * Set a document (create or overwrite)
   */
  async setDocument(collectionPath: string, documentId: string, data: any, merge: boolean = false): Promise<void> {
    const reference = `${collectionPath}/${documentId}`;

    if (this.isNative) {
      await FirebaseFirestore.setDocument({
        reference,
        data,
        merge,
      });
    } else {
      const db = getDb();
      const docRef = doc(db, collectionPath, documentId);
      await setDoc(docRef, data, { merge });
    }
  }

  /**
   * Update specific fields in a document
   */
  async updateDocument(collectionPath: string, documentId: string, data: any): Promise<void> {
    const reference = `${collectionPath}/${documentId}`;

    if (this.isNative) {
      await FirebaseFirestore.updateDocument({
        reference,
        data,
      });
    } else {
      const db = getDb();
      const docRef = doc(db, collectionPath, documentId);
      await updateDoc(docRef, data);
    }
  }

  /**
   * Delete a document
   */
  async deleteDocument(collectionPath: string, documentId: string): Promise<void> {
    const reference = `${collectionPath}/${documentId}`;

    if (this.isNative) {
      await FirebaseFirestore.deleteDocument({ reference });
    } else {
      const db = getDb();
      const docRef = doc(db, collectionPath, documentId);
      await deleteDoc(docRef);
    }
  }

  /**
   * Add a new document with auto-generated ID
   */
  async addDocument(collectionPath: string, data: any): Promise<string> {
    if (this.isNative) {
      const result = await FirebaseFirestore.addDocument({
        reference: collectionPath,
        data,
      });
      return result.reference.id;
    } else {
      const db = getDb();
      const collectionRef = collection(db, collectionPath);
      const docRef = await addDoc(collectionRef, data);
      return docRef.id;
    }
  }

  /**
   * Query documents in a collection
   * Note: Native plugin has different query syntax
   */
  async queryDocuments<T>(
    collectionPath: string,
    constraints: {
      field: string;
      operator: '==' | '!=' | '<' | '<=' | '>' | '>=' | 'array-contains' | 'in';
      value: any;
    }[],
    options?: {
      orderByField?: string;
      orderDirection?: 'asc' | 'desc';
      limitCount?: number;
    }
  ): Promise<Array<T & { id: string }>> {
    if (this.isNative) {
      try {
        // Build composite filter for native
        const queryConstraints = constraints.map(c => ({
          type: 'where' as const,
          fieldPath: c.field,
          opStr: c.operator,
          value: c.value,
        }));

        const result = await FirebaseFirestore.getCollection({
          reference: collectionPath,
          compositeFilter: constraints.length > 0 ? {
            type: 'and',
            queryConstraints,
          } : undefined,
          queryConstraints: [
            ...(options?.orderByField ? [{
              type: 'orderBy' as const,
              fieldPath: options.orderByField,
              directionStr: options.orderDirection || 'asc',
            }] : []),
            ...(options?.limitCount ? [{
              type: 'limit' as const,
              limit: options.limitCount,
            }] : []),
          ],
        });

        return result.snapshots.map(snap => ({
          id: snap.id,
          ...snap.data,
        } as T & { id: string }));
      } catch (error) {
        console.error('NativeFirebaseService: Error querying documents:', error);
        throw error;
      }
    } else {
      const db = getDb();
      const collectionRef = collection(db, collectionPath);

      // Build web query constraints
      const webConstraints: any[] = constraints.map(c => where(c.field, c.operator, c.value));

      if (options?.orderByField) {
        webConstraints.push(orderBy(options.orderByField, options.orderDirection || 'asc'));
      }

      if (options?.limitCount) {
        webConstraints.push(limit(options.limitCount));
      }

      const q = query(collectionRef, ...webConstraints);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as T & { id: string }));
    }
  }

  /**
   * Listen to document changes in real-time
   * Returns cleanup function
   */
  onDocumentSnapshot<T>(
    collectionPath: string,
    documentId: string,
    callback: (data: T | null) => void,
    onError?: (error: Error) => void
  ): () => void {
    const reference = `${collectionPath}/${documentId}`;

    if (this.isNative) {
      let callbackId: string | null = null;

      FirebaseFirestore.addDocumentSnapshotListener(
        { reference },
        (event, error) => {
          if (error) {
            console.error('NativeFirebaseService: Snapshot error:', error);
            onError?.(new Error(error.message));
            return;
          }
          callback(event?.snapshot?.data as T ?? null);
        }
      ).then(result => {
        // Result is the callbackId string directly
        callbackId = typeof result === 'string' ? result : (result as any).callbackId;
        this.snapshotListeners.set(reference, callbackId);
      });

      return () => {
        if (callbackId) {
          FirebaseFirestore.removeSnapshotListener({ callbackId });
          this.snapshotListeners.delete(reference);
        }
      };
    } else {
      const db = getDb();
      const docRef = doc(db, collectionPath, documentId);

      const unsubscribe = onSnapshot(
        docRef,
        (docSnap) => {
          callback(docSnap.exists() ? (docSnap.data() as T) : null);
        },
        (error) => {
          console.error('NativeFirebaseService: Snapshot error:', error);
          onError?.(error);
        }
      );

      return unsubscribe;
    }
  }

  /**
   * Listen to collection changes in real-time
   */
  onCollectionSnapshot<T>(
    collectionPath: string,
    callback: (data: Array<T & { id: string }>) => void,
    options?: {
      constraints?: {
        field: string;
        operator: '==' | '!=' | '<' | '<=' | '>' | '>=' | 'array-contains' | 'in';
        value: any;
      }[];
      orderByField?: string;
      orderDirection?: 'asc' | 'desc';
      limitCount?: number;
    },
    onError?: (error: Error) => void
  ): () => void {
    if (this.isNative) {
      let callbackId: string | null = null;

      FirebaseFirestore.addCollectionSnapshotListener(
        {
          reference: collectionPath,
          compositeFilter: options?.constraints?.length ? {
            type: 'and',
            queryConstraints: options.constraints.map(c => ({
              type: 'where' as const,
              fieldPath: c.field,
              opStr: c.operator,
              value: c.value,
            })),
          } : undefined,
          queryConstraints: [
            ...(options?.orderByField ? [{
              type: 'orderBy' as const,
              fieldPath: options.orderByField,
              directionStr: options.orderDirection || 'asc',
            }] : []),
            ...(options?.limitCount ? [{
              type: 'limit' as const,
              limit: options.limitCount,
            }] : []),
          ],
        },
        (event, error) => {
          if (error) {
            console.error('NativeFirebaseService: Collection snapshot error:', error);
            onError?.(new Error(error.message));
            return;
          }
          const docs = event?.snapshots?.map(snap => ({
            id: snap.id,
            ...snap.data,
          })) ?? [];
          callback(docs as Array<T & { id: string }>);
        }
      ).then(result => {
        // Result is the callbackId string directly
        callbackId = typeof result === 'string' ? result : (result as any).callbackId;
      });

      return () => {
        if (callbackId) {
          FirebaseFirestore.removeSnapshotListener({ callbackId });
        }
      };
    } else {
      const db = getDb();
      const collectionRef = collection(db, collectionPath);

      const webConstraints: any[] = options?.constraints?.map(c =>
        where(c.field, c.operator, c.value)
      ) ?? [];

      if (options?.orderByField) {
        webConstraints.push(orderBy(options.orderByField, options.orderDirection || 'asc'));
      }

      if (options?.limitCount) {
        webConstraints.push(limit(options.limitCount));
      }

      const q = query(collectionRef, ...webConstraints);

      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const docs = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));
          callback(docs as Array<T & { id: string }>);
        },
        (error) => {
          console.error('NativeFirebaseService: Collection snapshot error:', error);
          onError?.(error);
        }
      );

      return unsubscribe;
    }
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Get server timestamp
   * Works on both platforms
   */
  getServerTimestamp() {
    if (this.isNative) {
      // Native plugin handles this automatically
      return { _serverTimestamp: true };
    } else {
      return serverTimestamp();
    }
  }

  /**
   * Check if running on native platform
   */
  isPlatformNative(): boolean {
    return this.isNative;
  }

  /**
   * Clean up all listeners
   */
  async cleanup(): Promise<void> {
    if (this.isNative) {
      // Remove all snapshot listeners
      for (const callbackId of this.snapshotListeners.values()) {
        await FirebaseFirestore.removeSnapshotListener({ callbackId });
      }
      this.snapshotListeners.clear();

      // Remove auth listener
      await FirebaseAuthentication.removeAllListeners();
    }
  }
}

// Export singleton instance
export const nativeFirebase = new NativeFirebaseService();

// Export class for testing
export { NativeFirebaseService };
